import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TaskPanelComponent } from '../../shared/components/task/task-panel.component';
import { ProjectService } from '../../services/project.service';
import { ProjectStateService } from '../../services/project-state.service';
import { UserService } from '../../services/user.service';
import {
  ProjectTask,
  TaskPanelConfig,
  UserDetail
} from '../../models/task-management.models';
import { ProjectModel } from '../../models/project.model';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';

@Component({
  selector: 'app-tasks',
  standalone: true,
  imports: [
    CommonModule,
    TaskPanelComponent
  ],
  template: `
    <div class="tasks-page">
      <app-task-panel
        *ngIf="projectId && canView"
        [projectId]="projectId"
        [blockScheduleId]="0"
        [config]="taskConfig"
        [currentUser]="currentUser"
        (taskCreated)="onTaskCreated($event)"
        (taskUpdated)="onTaskUpdated($event)"
        (taskDeleted)="onTaskDeleted($event)"
        (taskSelected)="onTaskSelected($event)">
      </app-task-panel>

      <div class="error-state" *ngIf="error && !loading">
        <div class="error-content">
          <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #ef4444;"></i>
          <h3>Unable to Load Tasks</h3>
          <p>{{ error }}</p>
          <button type="button" class="p-button p-button-outlined" (click)="loadProjectData()">
            <i class="pi pi-refresh"></i>
            Retry
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
    }

    .tasks-page {
      flex: 1;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .loading-state,
    .error-state {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 400px;
      text-align: center;
    }

    .loading-content,
    .error-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      max-width: 400px;
      padding: 2rem;
    }

    .error-content h3 {
      margin: 0;
      color: var(--text-color);
      font-size: 1.25rem;
      font-weight: 600;
    }

    .error-content p {
      margin: 0;
      color: var(--text-color-secondary);
    }
  `]
})
export class TasksComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  projectId?: number;
  currentProject?: ProjectModel;
  currentUser?: UserDetail;
  loading = true;
  error?: string;
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;
  private isProjectInactive = false;

  taskConfig: TaskPanelConfig = {
    showCreateButton: true,
    showFilters: true,
    showKanbanView: true,
    allowTaskEdit: true,
    allowTaskDelete: true,
    allowComments: true,
    defaultView: 'kanban',
    compactMode: false
  };

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private projectStateService: ProjectStateService,
    private userService: UserService,
    private permissionHelper: PermissionHelperService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadRouteParams();
    this.loadCurrentUser();
    this.projectStateService.currentProjectUserActive$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.applyPermissions();
    });

    this.projectStateService.currentProjectPermissions$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.VIEW);
      this.canCreate = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.CREATE);
      this.canUpdate = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.UPDATE);
      this.canDelete = this.permissionHelper.has(PAGE_KEYS.TASKS, PERMISSION_KEYS.DELETE);
      this.applyPermissions();
    });
  }

  private applyPermissions(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canCreate && !this.canUpdate && !this.canDelete);
    this.taskConfig = {
      ...this.taskConfig,
      showCreateButton: this.canCreate && !this.isProjectInactive,
      allowTaskEdit: this.canUpdate && !this.isProjectInactive,
      allowTaskDelete: this.canDelete && !this.isProjectInactive,
      allowComments: this.canUpdate && !this.isProjectInactive
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRouteParams() {
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.projectId = params['projectId'] ? +params['projectId'] : undefined;
      if (this.projectId) {
        this.loadProjectData();
      }
    });
  }

  private loadCurrentUser() {
    this.userService.getUserList().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (userList) => {
        if (userList && userList.length > 0) {
          const user = userList[0];
          this.currentUser = {
            userId: user.userId || 0,
            name: `${user.firstName} ${user.lastName}`.trim(),
            email: user.email,
            role: user.roleName
          };
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        console.error('Error loading current user:', error);
      }
    });
  }

  loadProjectData() {
    if (!this.projectId) return;

    this.loading = true;
    this.error = undefined;

    this.projectService.getProjectById(this.projectId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (project) => {
        this.currentProject = project;
        if (!this.currentProject) {
          this.error = 'Project not found.';
        }
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading project data:', error);
        this.error = 'Failed to load project information. Please try again.';
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onTaskCreated(task: ProjectTask) {
    console.log('Task created:', task);
  }

  onTaskUpdated(task: ProjectTask) {
    console.log('Task updated:', task);
  }

  onTaskDeleted(taskId: number) {
    console.log('Task deleted:', taskId);
  }

  onTaskSelected(task: ProjectTask) {
    console.log('Task selected:', task);
  }
}
